import { useState, useEffect } from 'react';
import {
  ListChecks, Filter, Trash2, Edit3, Check, Clock, Zap, Info as InfoIcon,
  Newspaper, Radio, MapPin, Calendar, Plus, X, AlertCircle, Sparkles, Save,
  FileText,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  a_faire:  { label: 'À faire',  color: '#3B82F6', bg: 'bg-blue-50',   border: 'border-blue-200' },
  en_cours: { label: 'En cours', color: '#F59E0B', bg: 'bg-amber-50',  border: 'border-amber-200' },
  fait:     { label: 'Fait',     color: '#10B981', bg: 'bg-green-50',  border: 'border-green-200' },
  annule:   { label: 'Annulé',   color: '#94A3B8', bg: 'bg-gray-50',   border: 'border-gray-200' },
};

const PRIORITY_META = {
  urgent:     { label: 'Urgent',      color: '#DC2626', icon: Zap },
  recommande: { label: 'Recommandé',  color: '#F59E0B', icon: Clock },
  informatif: { label: 'Informatif',  color: '#3B82F6', icon: InfoIcon },
};

const SOURCE_META = {
  veille:         { label: 'Veille',       icon: Newspaper, color: '#1B2A4A' },
  radar:          { label: 'Radar santé',  icon: Radio,     color: '#DC2626' },
  ecosystem:      { label: 'Cartographie', icon: MapPin,    color: '#059669' },
  campaigns:      { label: 'Campagnes',    icon: Calendar,  color: '#B8860B' },
  cabinet_report: { label: 'Cabinet',      icon: FileText,  color: '#8B0028' },
  manual:         { label: 'Manuel',       icon: Plus,      color: '#7C3AED' },
};

function ActionRow({ action, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    notes: action.notes || '',
    due_date: action.due_date ? action.due_date.slice(0, 10) : '',
    priority: action.priority,
  });
  const stMeta = STATUS_META[action.status] || STATUS_META.a_faire;
  const prMeta = PRIORITY_META[action.priority] || PRIORITY_META.informatif;
  const PrIcon = prMeta.icon;
  const srMeta = SOURCE_META[action.source_module] || SOURCE_META.manual;
  const SrIcon = srMeta.icon;

  const handleSaveEdit = async () => {
    await onUpdate(action.id, draft);
    setEditing(false);
  };

  return (
    <div className={`bg-white rounded-xl border ${stMeta.border} overflow-hidden`} data-testid={`action-${action.id}`}
         style={{ borderLeft: `4px solid ${prMeta.color}` }}>
      <div className="p-3">
        <div className="flex items-start gap-2 mb-1">
          <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase text-white flex items-center gap-0.5" style={{ backgroundColor: prMeta.color }}>
            <PrIcon size={9} /> {prMeta.label}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-0.5" style={{ backgroundColor: `${srMeta.color}15`, color: srMeta.color }}>
            <SrIcon size={9} /> {srMeta.label}
          </span>
          {action.due_date && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase bg-gray-100 text-gray-600">
              {"\u00e9ch: "}{new Date(action.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
            </span>
          )}
        </div>
        <h4 className={`text-sm font-bold text-[#1B2A4A] ${action.status === 'fait' ? 'line-through opacity-60' : ''}`}>
          {action.title}
        </h4>
        {action.description && (
          <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{action.description}</p>
        )}

        {editing ? (
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              <input
                type="date"
                value={draft.due_date}
                onChange={e => setDraft({ ...draft, due_date: e.target.value })}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1"
                data-testid={`edit-due-${action.id}`}
              />
              <select
                value={draft.priority}
                onChange={e => setDraft({ ...draft, priority: e.target.value })}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1"
                data-testid={`edit-priority-${action.id}`}
              >
                <option value="urgent">Urgent</option>
                <option value="recommande">Recommandé</option>
                <option value="informatif">Informatif</option>
              </select>
            </div>
            <textarea
              placeholder="Note personnelle..."
              value={draft.notes}
              onChange={e => setDraft({ ...draft, notes: e.target.value })}
              data-testid={`edit-notes-${action.id}`}
              className="w-full text-xs border border-gray-200 rounded-lg p-2 focus:outline-none focus:border-[#1B2A4A] resize-none"
              rows={2}
            />
            <div className="flex gap-2">
              <button onClick={handleSaveEdit} data-testid={`save-edit-${action.id}`}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-[#1B2A4A] text-white text-[11px] font-bold">
                <Save size={11} /> Enregistrer
              </button>
              <button onClick={() => setEditing(false)}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-bold">
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <>
            {action.notes && (
              <p className="text-[11px] text-gray-600 mt-2 italic bg-gray-50 rounded p-2 border border-gray-100">"{action.notes}"</p>
            )}
            <div className="flex items-center gap-1 mt-2">
              {Object.keys(STATUS_META).map(k => (
                <button key={k} onClick={() => onUpdate(action.id, { status: k })}
                        className={`text-[10px] px-2 py-1 rounded-lg font-bold transition ${action.status === k ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                        style={action.status === k ? { backgroundColor: STATUS_META[k].color } : {}}
                        data-testid={`status-btn-${k}-${action.id}`}>
                  {STATUS_META[k].label}
                </button>
              ))}
              <div className="flex-1" />
              <button onClick={() => setEditing(true)} data-testid={`edit-btn-${action.id}`}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                <Edit3 size={12} />
              </button>
              <button onClick={() => onDelete(action.id)} data-testid={`delete-btn-${action.id}`}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-400">
                <Trash2 size={12} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ActionsTab() {
  const [data, setData] = useState({ actions: [], by_status: {}, by_source: {}, by_priority: {} });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem('benchory_actions_filter_status') || 'a_faire');
  const [filterSource, setFilterSource] = useState(() => localStorage.getItem('benchory_actions_filter_source') || 'all');
  const [creatingNew, setCreatingNew] = useState(false);
  const [newAction, setNewAction] = useState({ title: '', description: '', priority: 'recommande', due_date: '' });

  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Persist filters
  useEffect(() => { localStorage.setItem('benchory_actions_filter_status', filterStatus); }, [filterStatus]);
  useEffect(() => { localStorage.setItem('benchory_actions_filter_source', filterSource); }, [filterSource]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterSource !== 'all') params.set('source', filterSource);
      const res = await fetch(`${API_URL}/actions?${params}`, { headers });
      const j = await res.json();
      setData(j);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateAction = async (id, fields) => {
    try {
      const res = await fetch(`${API_URL}/actions/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (res.ok) load();
    } catch (e) { console.error(e); }
  };

  const deleteAction = async (id) => {
    if (!window.confirm('Supprimer cette action ?')) return;
    try {
      await fetch(`${API_URL}/actions/${id}`, { method: 'DELETE', headers });
      load();
    } catch (e) { console.error(e); }
  };

  const createAction = async () => {
    if (!newAction.title.trim()) return;
    try {
      const res = await fetch(`${API_URL}/actions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newAction.title,
          description: newAction.description,
          priority: newAction.priority,
          source_module: 'manual',
          due_date: newAction.due_date || null,
        }),
      });
      if (res.ok) {
        setCreatingNew(false);
        setNewAction({ title: '', description: '', priority: 'recommande', due_date: '' });
        load();
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterSource]);

  const counts = {
    all: data.total || 0,
    a_faire: data.by_status?.a_faire || 0,
    en_cours: data.by_status?.en_cours || 0,
    fait: data.by_status?.fait || 0,
  };

  return (
    <div className="space-y-4" data-testid="actions-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ListChecks size={14} className="text-[#1B2A4A] shrink-0" />
            <span className="text-xs font-bold text-[#1B2A4A]">Mes actions Benchory</span>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Toutes les actions issues de la Veille, du Radar, des Opportunités et des Campagnes
          </p>
        </div>
        <button
          onClick={() => setCreatingNew(true)}
          className="text-[11px] flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B2A4A] text-white font-bold hover:bg-[#0F1E37] min-h-[44px] sm:min-h-0 sm:py-1.5 shrink-0"
          data-testid="create-action-btn"
        >
          <Plus size={12} /> Nouvelle action
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { k: 'all', label: 'Total', value: counts.all, color: '#1B2A4A' },
          { k: 'a_faire', label: 'À faire', value: counts.a_faire, color: '#3B82F6' },
          { k: 'en_cours', label: 'En cours', value: counts.en_cours, color: '#F59E0B' },
          { k: 'fait', label: 'R\u00e9alis\u00e9es', value: counts.fait, color: '#10B981' },
          { k: 'annule', label: 'Annul\u00e9es', value: data.by_status?.annule || 0, color: '#94A3B8' },
        ].map(s => (
          <button key={s.k} onClick={() => setFilterStatus(s.k)}
                  className={`bg-white rounded-xl border-2 p-3 text-left transition ${filterStatus === s.k ? 'shadow-md' : 'border-gray-100'}`}
                  style={filterStatus === s.k ? { borderColor: s.color } : {}}
                  data-testid={`stat-${s.k}`}>
            <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[10px] text-gray-400 font-semibold uppercase">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Filter sources */}
      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter size={12} className="text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500 uppercase">Source</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setFilterSource('all')}
                  className={`px-2 py-1 rounded-full text-[10px] font-bold transition ${filterSource === 'all' ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-500'}`}
                  data-testid="filter-source-all">
            Toutes ({data.total || 0})
          </button>
          {Object.entries(SOURCE_META).map(([k, m]) => {
            const c = data.by_source?.[k] || 0;
            if (c === 0) return null;
            const Icon = m.icon;
            return (
              <button key={k} onClick={() => setFilterSource(k)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition ${filterSource === k ? 'text-white' : 'hover:opacity-80'}`}
                      style={filterSource === k ? { backgroundColor: m.color } : { backgroundColor: `${m.color}15`, color: m.color }}
                      data-testid={`filter-source-${k}`}>
                <Icon size={10} /> {m.label} ({c})
              </button>
            );
          })}
        </div>
      </div>

      {/* New action modal inline */}
      {creatingNew && (
        <div className="bg-white rounded-xl border-2 border-[#1B2A4A] p-4 space-y-2" data-testid="new-action-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#1B2A4A]">Nouvelle action</h3>
            <button onClick={() => setCreatingNew(false)} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
          </div>
          <input
            placeholder="Titre de l'action..."
            value={newAction.title}
            onChange={e => setNewAction({ ...newAction, title: e.target.value })}
            data-testid="new-action-title"
            className="w-full text-sm border border-gray-200 rounded-lg p-2 focus:outline-none focus:border-[#1B2A4A]"
          />
          <textarea
            placeholder="Description..."
            value={newAction.description}
            onChange={e => setNewAction({ ...newAction, description: e.target.value })}
            data-testid="new-action-desc"
            className="w-full text-xs border border-gray-200 rounded-lg p-2 focus:outline-none focus:border-[#1B2A4A] resize-none"
            rows={2}
          />
          <div className="flex gap-2">
            <select
              value={newAction.priority}
              onChange={e => setNewAction({ ...newAction, priority: e.target.value })}
              className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
              data-testid="new-action-priority"
            >
              <option value="urgent">Urgent</option>
              <option value="recommande">Recommandé</option>
              <option value="informatif">Informatif</option>
            </select>
            <input
              type="date"
              value={newAction.due_date}
              onChange={e => setNewAction({ ...newAction, due_date: e.target.value })}
              className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
              data-testid="new-action-due"
            />
            <button onClick={createAction} data-testid="new-action-save"
                    className="px-4 py-1.5 rounded-lg bg-[#1B2A4A] text-white text-xs font-bold">
              Créer
            </button>
          </div>
        </div>
      )}

      {/* Actions list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
        </div>
      ) : data.actions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <ListChecks size={36} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Aucune action {STATUS_META[filterStatus]?.label?.toLowerCase()}</p>
          <p className="text-[10px] text-gray-400 mt-1">
            Importez une action depuis la Veille, le Radar ou la Cartographie, ou créez-en une manuellement.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="actions-list">
          {data.actions.map(a => (
            <ActionRow key={a.id} action={a} onUpdate={updateAction} onDelete={deleteAction} />
          ))}
        </div>
      )}
    </div>
  );
}
